import React, { useState, useMemo } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const deg2rad = d => (d * Math.PI) / 180;

// SVG phasor diagram showing HV and LV phasors on the same circle
const PhasorDiagram = ({ shiftDeg, vHV, vLV, dark }) => {
  const CX = 120, CY = 120, R = 80;

  const axisColor = dark ? '#334155' : '#e2e8f0';
  const textColor = dark ? '#64748b' : '#94a3b8';
  const bgFill    = dark ? '#0f172a' : '#ffffff';

  const HV_PHASORS = [
    { label: 'Va',  deg: 0,    color: '#0d9488' },
    { label: 'Vb',  deg: -120, color: '#6366f1' },
    { label: 'Vc',  deg: 120,  color: '#f59e0b' },
  ];

  const pt = (deg, r = R) => ({
    x: CX + r * Math.cos(deg2rad(deg)),
    y: CY - r * Math.sin(deg2rad(deg)),
  });

  const arrowPts = (deg, r = R) => {
    const tip  = pt(deg, r);
    const base = pt(deg, r - 13);
    const offX =  6 * Math.sin(deg2rad(deg));
    const offY =  6 * Math.cos(deg2rad(deg));
    return `${tip.x},${tip.y} ${base.x + offX},${base.y + offY} ${base.x - offX},${base.y - offY}`;
  };

  return (
    <svg viewBox="0 0 240 240" className="w-full max-w-[230px] mx-auto block">
      <rect width="240" height="240" fill={bgFill} rx="10" />

      {/* Grid */}
      <circle cx={CX} cy={CY} r={R} fill="none" stroke={axisColor} strokeWidth="1" strokeDasharray="4 3" />
      <circle cx={CX} cy={CY} r={R * 0.6} fill="none" stroke={axisColor} strokeWidth="0.8" strokeDasharray="3 4" />
      <line x1={CX - R - 10} y1={CY} x2={CX + R + 10} y2={CY} stroke={axisColor} strokeWidth="1" />
      <line x1={CX} y1={CY - R - 10} x2={CX} y2={CY + R + 10} stroke={axisColor} strokeWidth="1" />
      <text x={CX + R + 4} y={CY - 4} fontSize="8.5" fill={textColor} fontFamily="IBM Plex Mono">+Re</text>
      <text x={CX + 4} y={CY - R - 2} fontSize="8.5" fill={textColor} fontFamily="IBM Plex Mono">+Im</text>

      {/* HV phasors — solid, outer ring */}
      {HV_PHASORS.map(({ label, deg, color }) => (
        <g key={`hv-${label}`}>
          <line x1={CX} y1={CY} x2={pt(deg, R - 13).x} y2={pt(deg, R - 13).y}
            stroke={color} strokeWidth="2.5" strokeLinecap="round" />
          <polygon points={arrowPts(deg)} fill={color} />
          <text x={pt(deg, R + 16).x} y={pt(deg, R + 16).y + 4}
            fontSize="11" fontWeight="700" fontFamily="IBM Plex Mono"
            fill={color} textAnchor="middle">
            {label}
          </text>
        </g>
      ))}

      {/* LV phasors — dashed, inner ring (60% of R) */}
      {HV_PHASORS.map(({ label, deg, color }) => {
        const lvDeg  = deg + shiftDeg;
        const lvR    = R * 0.6;
        const tipLV  = pt(lvDeg, lvR);
        const baseLV = pt(lvDeg, lvR - 10);
        const offX   = 4.5 * Math.sin(deg2rad(lvDeg));
        const offY   = 4.5 * Math.cos(deg2rad(lvDeg));
        // lighten color for LV
        const lvColor = color === '#0d9488' ? '#5eead4'
          : color === '#6366f1' ? '#a5b4fc'
          : '#fcd34d';
        return (
          <g key={`lv-${label}`}>
            <line x1={CX} y1={CY} x2={baseLV.x} y2={baseLV.y}
              stroke={lvColor} strokeWidth="2" strokeDasharray="5 2" strokeLinecap="round" />
            <polygon
              points={`${tipLV.x},${tipLV.y} ${baseLV.x + offX},${baseLV.y + offY} ${baseLV.x - offX},${baseLV.y - offY}`}
              fill={lvColor}
            />
            <text x={pt(lvDeg, lvR + 13).x} y={pt(lvDeg, lvR + 13).y + 4}
              fontSize="9" fontFamily="IBM Plex Mono"
              fill={lvColor} textAnchor="middle">
              {label}'
            </text>
          </g>
        );
      })}

      {/* Phase shift arc (Va reference = 0°) */}
      {shiftDeg !== 0 && (() => {
        const r0  = 30;
        const from = pt(0, r0);
        const to   = pt(shiftDeg, r0);
        const sweep = shiftDeg > 0 ? 1 : 0;
        return (
          <>
            <path
              d={`M ${from.x} ${from.y} A ${r0} ${r0} 0 0 ${sweep} ${to.x} ${to.y}`}
              fill="none" stroke="#8b5cf6" strokeWidth="1.5"
            />
            <text
              x={pt(shiftDeg / 2, r0 + 11).x}
              y={pt(shiftDeg / 2, r0 + 11).y + 4}
              fontSize="9" fill="#8b5cf6" fontFamily="IBM Plex Mono" textAnchor="middle">
              {shiftDeg > 0 ? '+' : ''}{shiftDeg}°
            </text>
          </>
        );
      })()}

      {/* Origin dot */}
      <circle cx={CX} cy={CY} r="3" fill={dark ? '#475569' : '#94a3b8'} />

      {/* Legend */}
      <line x1="12" y1="218" x2="32" y2="218" stroke="#0d9488" strokeWidth="2.5" />
      <text x="36" y="222" fontSize="8.5" fill={textColor} fontFamily="IBM Plex Sans">HV (Va,Vb,Vc)</text>
      <line x1="12" y1="230" x2="32" y2="230" stroke="#5eead4" strokeWidth="2" strokeDasharray="5 2" />
      <text x="36" y="234" fontSize="8.5" fill={textColor} fontFamily="IBM Plex Sans">LV (Va',Vb',Vc')</text>
    </svg>
  );
};

const VECTOR_GROUPS = [
  { key: 'Dyn11', label: 'Dyn11', shift: 30,  desc: 'Delta HV / Wye+N LV — LV leads HV by 30°. Most common in UK/EU distribution.' },
  { key: 'Dyn1',  label: 'Dyn1',  shift: -30, desc: 'Delta HV / Wye+N LV — LV lags HV by 30°. Common in North America.' },
  { key: 'YNyn0', label: 'YNyn0', shift: 0,   desc: 'Wye+N HV / Wye+N LV — no phase shift. Used for autotransformers and grounding.' },
];

const DeltaWyePhaseShiftCalc = () => {
  const { dark } = useTheme();
  const [vg, setVg]   = useState('Dyn11');
  const [vHV, setVHV] = useState(138);
  const [vLV, setVLV] = useState(13.8);

  const selected = VECTOR_GROUPS.find(g => g.key === vg);
  const shift    = selected ? selected.shift : 0;

  const table = useMemo(() => {
    const base = [0, -120, 120];
    return [
      { hv: 'Va', lv: "Va'", hvDeg: 0,    lvDeg: shift },
      { hv: 'Vb', lv: "Vb'", hvDeg: -120, lvDeg: -120 + shift },
      { hv: 'Vc', lv: "Vc'", hvDeg: 120,  lvDeg:  120 + shift },
    ];
  }, [shift]);

  const normDeg = d => {
    let n = ((d % 360) + 360) % 360;
    if (n > 180) n -= 360;
    return n;
  };

  const axisColor = dark ? '#64748b' : '#94a3b8';

  return (
    <div data-testid="delta-wye-phase-shift-calc" className="space-y-5">

      {/* Vector group selector */}
      <div>
        <div className="flex items-center gap-1 mb-2">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Vector Group</p>
          <TeachingTooltip
            showIcon
            whatThis="The vector group encodes the winding connection type (D=delta, Y=wye, N=neutral) and the phase shift between HV and LV windings (clock notation: × 30°)."
            tryThis="Switch between Dyn11 and Dyn1 — notice the LV phasors (dashed) flip from leading to lagging the HV phasors by 30°."
            whyMatters="Transformer differential relays must compensate for the 30° phase shift. Incorrect vector group entry causes the relay to see an apparent imbalance and may trip on energization."
            connectedTo="→ Sets the angular offset between HV and LV phasors in the diagram and the table below."
          />
        </div>
        <div className="flex flex-wrap gap-3">
          {VECTOR_GROUPS.map(({ key, label }) => (
            <button
              key={key}
              data-testid={`vg-btn-${key}`}
              onClick={() => setVg(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium font-mono transition-colors border ${
                vg === key
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-teal-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {selected && (
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2">
            <span className="font-semibold text-teal-700 dark:text-teal-400">{selected.key}: </span>{selected.desc}
          </p>
        )}
      </div>

      {/* Voltage inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">V<sub>LL</sub> HV (kV)</label>
            <TeachingTooltip
              showIcon
              whatThis="The line-to-line voltage on the HV side of the transformer (the primary side for step-down, secondary side for step-up)."
              tryThis="Enter 138 kV — a typical sub-transmission voltage feeding a distribution substation."
              whyMatters="The HV voltage rating appears on the transformer nameplate and determines the turns ratio."
              connectedTo="→ Used with V_LV to compute turns ratio a = V_HV/V_LV in the table below."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="vhv-input" type="range" min="1" max="765" step="0.1" value={vHV}
              onChange={e => setVHV(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={vHV} step="0.1" min="1"
              onChange={e => setVHV(Math.max(1, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-7">kV</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">V<sub>LL</sub> LV (kV)</label>
            <TeachingTooltip
              showIcon
              whatThis="The line-to-line voltage on the LV side. This side's phasors carry the phase shift applied by the vector group."
              tryThis="Enter 13.8 kV — after the 30° shift (Dyn11), Va' at 13.8 kV ∠30° is not in phase with Va at 138 kV ∠0°."
              whyMatters="Relay engineers must know both the voltage magnitude ratio AND the 30° phase shift to correctly set differential protection."
              connectedTo="→ LV phasor magnitudes shown in the table; angles include the vector group phase shift."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="vlv-input" type="range" min="0.1" max="345" step="0.1" value={vLV}
              onChange={e => setVLV(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={vLV} step="0.1" min="0.1"
              onChange={e => setVLV(Math.max(0.1, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-7">kV</span>
          </div>
        </div>
      </div>

      {/* Diagram + table */}
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-full sm:w-auto flex-shrink-0">
          <p className="text-xs text-slate-500 dark:text-slate-500 mb-1.5 text-center">
            Phasor diagram — {vg} | shift = {shift >= 0 ? '+' : ''}{shift}°
          </p>
          <PhasorDiagram shiftDeg={shift} vHV={vHV} vLV={vLV} dark={dark} />
        </div>

        <div className="flex-1 w-full space-y-3">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-500">Phasor correspondence table</p>
          <div className="overflow-x-auto">
            <table data-testid="phase-shift-table" className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">HV Phasor</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">HV Angle</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">LV Phasor</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">LV Angle</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">Shift</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {table.map(({ hv, lv, hvDeg, lvDeg }) => (
                  <tr key={hv} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="py-1.5 px-2 font-mono font-bold text-teal-700 dark:text-teal-400">{hv}</td>
                    <td className="py-1.5 px-2 font-mono text-slate-700 dark:text-slate-300">
                      {vHV.toFixed(1)} kV ∠{normDeg(hvDeg)}°
                    </td>
                    <td className="py-1.5 px-2 font-mono font-bold text-teal-500 dark:text-teal-300">{lv}</td>
                    <td className="py-1.5 px-2 font-mono text-slate-700 dark:text-slate-300">
                      {vLV.toFixed(1)} kV ∠{normDeg(lvDeg) >= 0 ? '+' : ''}{normDeg(lvDeg)}°
                    </td>
                    <td className="py-1.5 px-2 font-mono text-purple-700 dark:text-purple-400 font-semibold">
                      {shift >= 0 ? '+' : ''}{shift}°
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-md p-3">
            <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
              <span className="font-semibold">Relay note: </span>
              {shift !== 0
                ? `Differential relay current inputs from HV and LV CTs must be phase-shifted by ${Math.abs(shift)}° to compensate before comparison. Failure to compensate causes false trips on every energization and through-fault.`
                : 'YNyn0 has no phase shift — differential relay CTs on both sides can be connected with matching polarity. Zero-sequence current can flow on both sides (both have neutrals).'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeltaWyePhaseShiftCalc;
